/**
 * MenuItemForm - Form for adding new menu items
 */
import { motion } from "framer-motion";
import { Save, Trophy, Calendar, ExternalLink, Eye, Radio } from "lucide-react";
import { Button } from "../../ui/button";
import { PREDEFINED_PAGES } from "./constants";

export const MenuItemForm = ({
  formData,
  setFormData,
  selectedPage,
  setSelectedPage,
  selectedEvent,
  setSelectedEvent,
  events,
  handlePageSelect,
  handleEventSelect,
  handleCreate,
  onCancel,
  saving
}) => {
  // Handle system dropdown selection
  const handleSystemPageSelect = (pageName) => {
    const page = PREDEFINED_PAGES.find(p => p.name === pageName);
    if (page && page.type === 'system') {
      setSelectedPage(pageName);
      setSelectedEvent("");
      setFormData({ 
        ...formData, 
        name: page.name.replace(' (Dropdown)', ''), 
        href: page.href, 
        is_external: false, 
        item_type: 'system',
        has_dropdown: true,
        icon: page.icon,
        event_id: "" 
      });
    } else {
      handlePageSelect(pageName);
    }
  };

  return (
    <motion.form 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleCreate} 
      className="p-6 bg-zinc-900/50 border border-white/10 space-y-4"
    >
      <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Add New Menu Item</h3>
      
      {/* Type Selector */}
      <div className="grid grid-cols-2 gap-4">
        {/* Page Selector */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Select Page or Link Type</label>
          <select 
            value={selectedPage}
            onChange={(e) => handleSystemPageSelect(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">-- Select a page --</option>
            <optgroup label="System Dropdowns (with icons)">
              {PREDEFINED_PAGES.filter(p => p.group === "System").map(page => (
                <option key={page.name} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Main Pages">
              {PREDEFINED_PAGES.filter(p => p.group === "Main").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Channels & Media">
              {PREDEFINED_PAGES.filter(p => p.group === "Media").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Events & Sports">
              {PREDEFINED_PAGES.filter(p => p.group === "Events").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="User Pages">
              {PREDEFINED_PAGES.filter(p => p.group === "User").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Info Pages">
              {PREDEFINED_PAGES.filter(p => p.group === "Info").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Portals">
              {PREDEFINED_PAGES.filter(p => p.group === "Portals").map(page => (
                <option key={page.href} value={page.name}>{page.name}</option>
              ))}
            </optgroup>
            <optgroup label="Custom">
              <option value="Custom Link">Custom External Link</option>
            </optgroup>
          </select>
        </div>

        {/* Event Selector */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
            Or Add an Event
            <Trophy className="w-3 h-3 inline ml-1 text-yellow-500" />
          </label>
          <select 
            value={selectedEvent}
            onChange={(e) => handleEventSelect(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">-- Select an event --</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name} ({event.sport})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Preview for System Dropdown */}
      {formData.item_type === "system" && formData.has_dropdown && (
        <div className="p-4 bg-zinc-800/50 border border-blue-500/30 space-y-2">
          <div className="flex items-center gap-3">
            {formData.icon === 'Radio' && <Radio className="w-5 h-5 text-blue-500" />}
            {formData.icon === 'Trophy' && <Trophy className="w-5 h-5 text-blue-500" />}
            <div>
              <div className="text-white font-bold text-sm">{formData.name}</div>
              <div className="text-xs text-zinc-500">
                This will appear as a dropdown menu with dynamic content
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview for Event */}
      {formData.item_type === "event" && formData.event_id && (
        <div className="p-4 bg-zinc-800/50 border border-yellow-500/30 space-y-3">
          <div className="flex items-center gap-3">
            {formData.event_logo_url && (
              <img src={formData.event_logo_url} alt="" className="w-10 h-10 object-contain" />
            )}
            <div>
              <div className="text-white font-bold text-sm">{formData.name}</div>
              <div className="text-xs text-zinc-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formData.event_dates || "No dates set"}
              </div>
            </div>
            <Trophy className="w-4 h-4 text-yellow-500 ml-auto" />
          </div>
          
          {/* Display Mode Selector */}
          {formData.event_logo_url && (
            <div className="pt-3 border-t border-yellow-500/20">
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Display Mode in Menu</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="display_mode" 
                    value="name" 
                    checked={formData.display_mode === "name"}
                    onChange={() => setFormData({ ...formData, display_mode: "name" })}
                    className="text-orange-500"
                  />
                  <span className="text-sm text-white">Show Name</span>
                  <span className="text-xs px-2 py-0.5 bg-zinc-700 text-zinc-300">{formData.name}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    name="display_mode" 
                    value="logo" 
                    checked={formData.display_mode === "logo"}
                    onChange={() => setFormData({ ...formData, display_mode: "logo" })}
                    className="text-orange-500"
                  />
                  <span className="text-sm text-white">Show Logo</span>
                  <img src={formData.event_logo_url} alt="" className="h-5 w-auto object-contain bg-zinc-700 px-2" />
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Name & Link Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Display Name</label>
          <input 
            type="text" 
            value={formData.name} 
            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
            placeholder="Menu item name" 
            required 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Link URL</label>
          <input 
            type="text" 
            value={formData.href} 
            onChange={(e) => setFormData({ ...formData, href: e.target.value })} 
            placeholder="/page or https://..." 
            required 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
      </div>

      {/* Options */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
          <input 
            type="checkbox" 
            checked={formData.is_external} 
            onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })} 
            className="rounded"
          />
          <ExternalLink className="w-4 h-4" />
          External Link (opens in new tab)
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
          <input 
            type="checkbox" 
            checked={formData.is_visible !== false} 
            onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })} 
            className="rounded"
          />
          <Eye className="w-4 h-4" />
          Visible in menu
        </label>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <Button type="submit" disabled={saving} className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Adding..." : "Add Item"}
        </Button>
        <Button type="button" onClick={onCancel} className="bg-zinc-800 text-white hover:bg-zinc-700 text-xs px-4 py-2 h-auto rounded-none">
          Cancel
        </Button>
      </div>
    </motion.form>
  );
};

export default MenuItemForm;
